import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { ScoreCardInfoDialogComponent } from '../../golf-scorecard/score-card-info-dialog/score-card-info-dialog.component';
import { GolfScoreCardModel } from 'src/app/_interfaces/golf/golf-score-card';
import { GolfPlayer } from 'src/app/_interfaces';
import { GameType, HoleResult } from 'src/app/_interfaces/golf/golf-tournament';
import { MatchplayPlayoffTableComponent } from '../../matchplay-playoff-table/matchplay-playoff-table.component';
import { AppStringComponent } from '../../app-string/app-string.component';


@Component({
    selector: 'app-matchplay-scorecard',
    imports: [CommonModule, MatIconModule, MatDialogModule, AppStringComponent, MatchplayPlayoffTableComponent],
    templateUrl: './matchplay-scorecard.component.html',
    styleUrls: ['./matchplay-scorecard.component.scss']
})
export class MatchPlayScorecardComponent {
    @Input() isFinished = false;
    @Input({ required: true }) left: GolfScoreCardModel[] = [];
    @Input({ required: true }) right: GolfScoreCardModel[] = [];
    @Input() aggregate: HoleResult[] | null = null;
    @Input() gameType: GameType | null = null;
    @Input() extraAggregate: HoleResult[] | null = null;

    holes = Array.from({ length: 18 }, (_, i) => i + 1);

    view: 'details' | 'overview' = 'details';

    constructor(private dialog: MatDialog) { }

    openInfoDialog(ev: MouseEvent): void {
        ev.stopPropagation();
        this.dialog.open(ScoreCardInfoDialogComponent, {
            panelClass: 'scorecard-info-dialog',
            autoFocus: false
        });
    }

    private rf(p?: GolfScoreCardModel | null) {
        const r = p?.round_final ?? null;
        return {
            pars: Array.isArray(r?.course_pars) ? r.course_pars as Array<number | null> : Array(18).fill(null),
            scores: Array.isArray(r?.scores) ? r.scores as Array<number | null> : Array(18).fill(null)
        };
    }

    parAt(i: number): number | string {
        const l = this.left[0] ? this.rf(this.left[0]).pars[i] : null;
        const r = this.right[0] ? this.rf(this.right[0]).pars[i] : null;
        return (l ?? r ?? '-') as number | string;
    }

    scoreAt(p: GolfScoreCardModel, i: number): string {
        const s = this.rf(p).scores[i];
        return s == null ? '-' : String(s);
    }

    shortName(p: GolfPlayer | undefined): string {
        if (!p) return '';
        const f = (p.first_name || '').trim();
        const l = (p.last_name || '').trim();
        return `${f ? f[0] + '.' : ''} ${l}`.trim().toUpperCase();
    }

    overlayClass(scoreTxt: string, parVal: number | string): string | null {
        if (scoreTxt === '-' || parVal === '-') return null;
        const diff = Number(scoreTxt) - Number(parVal);
        if (diff === -1) return 'single-circle';
        if (diff <= -2) return 'double-circle';
        if (diff === 1) return 'single-box';
        if (diff >= 2) return 'double-box';
        return null;
    }

    scoreLabel(i: number): string {
        const a = this.aggregate?.[i];
        return (a?.score ?? '-')!.toString().toUpperCase();
    }

    leaderColor(i: number): 'eu' | 'us' | 'team1' | 'team2' | null {
        const v = (this.aggregate?.[i]?.leader || '').toLowerCase();
        if (this.isRyderCupMode) {
            if (v === 'europe') return 'eu';
            if (v === 'usa') return 'us';
            return null;
        }
        if (v === 'team_1') return 'team1';
        if (v === 'team_2') return 'team2';
        return null;
    }

    holeWinner(i: number): 'eu' | 'us' | 'team1' | 'team2' | null {
        const v = (this.aggregate?.[i]?.hole_winner || '').toLowerCase();
        if (this.isRyderCupMode) {
            if (v === 'europe') return 'eu';
            if (v === 'usa') return 'us';
            return null;
        }
        if (v === 'team_1') return 'team1';
        if (v === 'team_2') return 'team2';
        return null;
    }

    winnerClass(side: 'left' | 'right', i: number): string {
        const w = this.holeWinner(i);
        if (!w) return '';
        const leftTeam = this.sideTeam('left');
        const rightTeam = this.sideTeam('right');
        const sideWon = (side === 'left' && leftTeam === w) || (side === 'right' && rightTeam === w);
        if (!sideWon) return '';
        if (w === 'eu') return 'win-eu';
        if (w === 'us') return 'win-us';
        if (w === 'team1') return 'win-team1';
        if (w === 'team2') return 'win-team2';
        return '';
    }

    getCellClasses(_: 'left' | 'right', p: GolfScoreCardModel, i: number): string[] {
        const overlay = this.overlayClass(this.scoreAt(p, i), this.parAt(i));
        return [overlay || ''].filter(Boolean) as string[];
    }

    private sideTeam(side: 'left' | 'right'): 'eu' | 'us' | 'team1' | 'team2' {
        if (this.isRyderCupMode) {
            const arr = side === 'left' ? this.left : this.right;
            const t = (arr?.[0]?.player?.ryder_cup_team || '').toLowerCase();
            if (t === 'usa') return 'us';
            return 'eu';
        }
        // Standard MP: left == team_1, right == team_2
        return side === 'left' ? 'team1' : 'team2';
    }

    private firstCardWithScores(arr: GolfScoreCardModel[]): GolfScoreCardModel | null {
        for (const m of arr) {
            const scores = this.rf(m).scores;
            if (scores.some(v => v != null)) return m;
        }
        return arr[0] ?? null;
    }

    teamScoreAt(side: 'left' | 'right', i: number): string {
        const arr = side === 'left' ? this.left : this.right;
        const card = this.firstCardWithScores(arr);
        if (!card) return '-';
        const s = this.rf(card).scores[i];
        return s == null ? '-' : String(s);
    }

    teamPlayers(side: 'left' | 'right'): GolfPlayer[] {
        const arr = side === 'left' ? this.left : this.right;
        return arr.map(x => x.player).filter(Boolean) as GolfPlayer[];
    }

    getTeamCellClasses(side: 'left' | 'right', i: number): string[] {
        const overlay = this.overlayClass(this.teamScoreAt(side, i), this.parAt(i));
        return [overlay || ''].filter(Boolean) as string[];
    }

    get isFoursomes(): boolean {
        return (this.gameType || '').toLowerCase() === 'four_some';
    }

    get isOverview(): boolean { return this.view === 'overview'; }

    toggleView(): void {
        this.view = this.view === 'details' ? 'overview' : 'details';
    }

    private get isRyderCupMode(): boolean {
        // Look at aggregate labels first
        for (const h of this.aggregate ?? []) {
            const lead = (h?.leader || '').toLowerCase();
            const win = (h?.hole_winner || '').toLowerCase();
            if (lead === 'europe' || lead === 'usa' || win === 'europe' || win === 'usa') return true;
            if (lead === 'team_1' || lead === 'team_2' || win === 'team_1' || win === 'team_2') return false;
        }
        // Fallback to players
        const tL = (this.left?.[0]?.player?.ryder_cup_team || '').toLowerCase();
        const tR = (this.right?.[0]?.player?.ryder_cup_team || '').toLowerCase();
        return tL === 'europe' || tL === 'usa' || tR === 'europe' || tR === 'usa';
    }

    mergeClasses(...items: Array<string | string[] | null | undefined>): string[] {
        const out: string[] = [];
        for (const it of items) {
            if (Array.isArray(it)) out.push(...(it.filter(Boolean) as string[]));
            else if (it) out.push(it);
        }
        return out;
    }

    isHoleNotPlayed(i: number): boolean {
        const h = this.aggregate?.[i];
        return !!h && h.leader == null && h.hole_winner == null && h.score == null;
    }
}
